/**
 * Accepted file formats for the /import-reference-estimates ("infer past deals") upload, as one
 * shared contract. The skill (@protoss/ai) advertises these as the file-prompt accept set, the
 * upload route (apps/api) gates against them, and the chat drawer (apps/web) filters with them -
 * so the three layers can never drift. The reference-estimate parser (detectReferenceFormat ->
 * parseReferenceEstimate) already understands every format below; this list is the door, not the
 * parser. MIME strings are inlined (shared is the base package and cannot import @protoss/doc),
 * mirroring TRANSCRIPT_ACCEPT_MIME in ../constants/index.ts.
 *
 * Keep REFERENCE_ESTIMATE_EXTENSIONS in lock-step with the extension checks in
 * detectReferenceFormat (apps/api/src/services/sales/reference-estimate-parse.ts).
 */
/** MIME types accepted for a reference-estimate upload (spreadsheet family + Word + PDF). */
export const REFERENCE_ESTIMATE_MIME_TYPES = [
    // Spreadsheets
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
    'application/vnd.ms-excel', // .xls (also sent for some .csv)
    'application/vnd.ms-excel.sheet.macroEnabled.12', // .xlsm
    'application/vnd.ms-excel.sheet.binary.macroEnabled.12', // .xlsb
    'application/vnd.oasis.opendocument.spreadsheet', // .ods
    'text/csv', // .csv
    // Documents (already parseable by the docx/pdf paths, previously un-uploadable)
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document', // .docx
    'application/msword', // .doc (legacy; surfaces the friendly "re-save" error if unreadable)
    'application/pdf', // .pdf
];
/**
 * Filename extensions accepted for a reference-estimate upload - the octet-stream fallback when a
 * browser sends a generic/blank MIME (common for drag-dropped .csv / .xls). Must match the
 * extension list in detectReferenceFormat.
 */
export const REFERENCE_ESTIMATE_EXTENSIONS = [
    '.xlsx',
    '.xlsm',
    '.xls',
    '.xlsb',
    '.ods',
    '.csv',
    '.docx',
    '.doc',
    '.pdf',
];
/**
 * Spreadsheet-only subset of {@link REFERENCE_ESTIMATE_EXTENSIONS} - what a SCOPE LIST upload accepts
 * (docs/plans/27). Deliberately narrower than the reference-estimate list: the scope parser reads a
 * grid of rows, so .docx/.doc/.pdf have nowhere to land and are better refused with a clear reason
 * than passed to a workbook reader that will fail on them. Also used by the create-deal upload card
 * to tell a scope sheet apart from a transcript.
 */
export const SCOPE_FILE_EXTENSIONS = [
    '.xlsx',
    '.xlsm',
    '.xls',
    '.xlsb',
    '.ods',
    '.csv',
    // Apple Numbers: the SheetJS reader handles it, and partners on Macs do send them.
    '.numbers',
];
/** True when this file looks like a scope spreadsheet (MIME or extension, same tolerance as below). */
export function isScopeFileUpload(mimeType, fileName) {
    const lower = (fileName ?? '').toLowerCase();
    if (SCOPE_FILE_EXTENSIONS.some((ext) => lower.endsWith(ext)))
        return true;
    // Spreadsheet MIMEs only - a generic/blank type falls back to the extension check above.
    const mime = (mimeType ?? '').toLowerCase();
    return (mime === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' ||
        mime === 'application/vnd.ms-excel' ||
        mime === 'application/vnd.oasis.opendocument.spreadsheet' ||
        mime === 'application/vnd.apple.numbers' ||
        mime === 'text/csv');
}
/**
 * Accept a reference-estimate upload by MIME, falling back to the filename extension when the
 * browser sends a generic type (e.g. application/octet-stream). Mirrors isAcceptedTranscriptUpload
 * (apps/api/src/routes/sales.ts) - the same tolerant MIME-or-extension gate.
 */
export function isAcceptedReferenceEstimateUpload(mimeType, fileName) {
    // Media types are case-insensitive (RFC 2045); normalise before the lookup.
    const mime = (mimeType ?? '').toLowerCase();
    if (REFERENCE_ESTIMATE_MIME_TYPES.includes(mime))
        return true;
    const lower = (fileName ?? '').toLowerCase();
    return REFERENCE_ESTIMATE_EXTENSIONS.some((ext) => lower.endsWith(ext));
}
//# sourceMappingURL=reference-estimate-upload.js.map